/**
 * Logins: the accounts people sign in with.
 *
 * Kept apart from employees on purpose, because they are different facts. An
 * employee is somebody on the payroll with a timesheet; a login is how somebody
 * gets into the product, and it is what holds roles. A kitchen porter clocks in
 * with a PIN and never has a login; an accountant has a login and is not staff.
 * The two meet at `Employee.user_id`, which is the only way Team can offer
 * roles to a member of staff.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export interface Login {
  id: string;
  /** Null for somebody who only works a till and signs in by PIN. */
  email: string | null;
  name: string;
  /** A deactivated login cannot sign in and should not be offered for linking. */
  is_active: boolean;
}

/** Everybody with a login here. Whether one is linked is `Employee.user_id`. */
export function useLogins({ enabled = true }: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: ['users', 'list'],
    queryFn: ({ signal }) => api.get<Login[]>('/v1/users', signal),
    // Reading the logins needs `identity.user.read`, which a waiter has no
    // business holding — so a caller who only wants names when a manager is
    // signed in says so rather than firing a request that will 403.
    enabled,
    staleTime: 60_000,
  });
}

/** The floor the server keeps, shared by new logins and every change. */
export const PASSWORD_MIN = 12;

/**
 * Make a login. Counts against `users.max`.
 *
 * The password is set by whoever creates it and has to reach the person some
 * other way — say so where it is typed, because nothing in the product sends
 * it. They can change it themselves once they are in.
 */
export function useCreateLogin() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Login, ApiError, { email: string; name: string; password: string }>({
    mutationFn: (input) => api.post<Login>('/v1/users', input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * Change your own password.
 *
 * The current one is asked for even though the caller is signed in. An access
 * token lives fifteen minutes and a tablet gets left unlocked on a pass; the
 * whole point of a change is that afterwards one person knows it. A wrong
 * current password is a 401 — the only one in the product that does not mean
 * "sign in again", which is why this is a plain mutation the caller reads the
 * error from rather than something the client's session handling sees.
 *
 * **It ends every session that login had, on every device, including this one.**
 * So the caller signs out on success and signs back in with the new one.
 */
export function useChangeOwnPassword() {
  const api = useApi();
  return useMutation<void, ApiError, { current_password: string; new_password: string }>({
    mutationFn: (input) =>
      api.post<void>('/v1/auth/password', input, { unauthenticatedEndsSession: false }),
  });
}

/**
 * Set somebody else's password, for the person who has forgotten theirs.
 *
 * There is no mailbox to send a reset link to, so recovery works the way a
 * forgotten door code does: ask the manager standing there who knows your face.
 * Say that on the screen rather than implying an email is coming.
 *
 * This signs them out everywhere too, which on a shift means their till. Worth
 * warning about before it happens rather than explaining afterwards.
 */
export function useSetUserPassword() {
  const api = useApi();
  return useMutation<void, ApiError, { userId: string; new_password: string }>({
    mutationFn: ({ userId, new_password }) =>
      api.post<void>(`/v1/users/${userId}/password`, { new_password }),
  });
}

/**
 * Ask for a link to set a new password. The answer is the same whether or not
 * the address has a login, so the screen can only ever say "check your email".
 */
export function useForgotPassword() {
  const api = useApi();
  return useMutation<void, ApiError, { tenant_slug: string; email: string }>({
    mutationFn: async (input) => {
      await api.post<null>('/v1/auth/password/forgot', input);
    },
  });
}

/** Set a new password with the token from an emailed link. Good once. */
export function useResetPasswordWithLink() {
  const api = useApi();
  return useMutation<
    void,
    ApiError,
    { tenant_slug: string; token: string; new_password: string }
  >({
    mutationFn: async (input) => {
      await api.post<null>('/v1/auth/password/reset', input);
    },
  });
}
